import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotifications,
  getNotificationsSummary,
  logoutUser,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/api";
import { clearAuthSession, isAuthenticated } from "../utils/auth";
import "./AppLayout.css";

const LOGO_SRC = "/logoamicale.png";

export default function TopBar() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || "Utilisateur";
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countsByType, setCountsByType] = useState({});
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const [listRes, summaryRes] = await Promise.all([
        getNotifications(),
        getNotificationsSummary(),
      ]);
      setNotifications(listRes.data || []);
      setUnreadCount(summaryRes.data?.unread_count || 0);
      setCountsByType(summaryRes.data?.by_type || {});
    } catch (error) {
      // Keep the top bar quiet if auth expired; other screens will handle it.
    }
  }, []);

  useEffect(() => {
    loadNotifications();

    const interval = window.setInterval(loadNotifications, 5000);
    const refreshOnFocus = () => loadNotifications();
    window.addEventListener("focus", refreshOnFocus);
    window.addEventListener("amicale:notifications-refresh", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOnFocus);
      window.removeEventListener("amicale:notifications-refresh", refreshOnFocus);
    };
  }, [loadNotifications]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (e) {
      // Logout should continue locally even if the server session already expired.
    }
    clearAuthSession();
    window.location.href = "/";
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    try {
      if (!notification.is_read) {
        const res = await markNotificationRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) => (item.id === notification.id ? res.data : item))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      loadNotifications();
    }
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      loadNotifications();
    }
  };

  return (
    <header className="app-topbar">
      <div className="app-topbar-brand">
        <img src={LOGO_SRC} alt="Logo Amicale Autoroutes" />
        <div>
          <span>Amicale Autoroutes</span>
          <strong>Tableau de bord</strong>
        </div>
      </div>

      <div className="app-topbar-actions">
        {isAuthenticated() && (
          <div className="app-notifications" ref={dropdownRef}>
            <button
              type="button"
              className={`app-notification-button ${unreadCount > 0 ? "has-unread" : ""}`}
              onClick={() => setOpen((value) => !value)}
              aria-label="Notifications"
            >
              <BellIcon />
              {unreadCount > 0 && <span>{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>

            {open && (
              <div className="app-notification-panel">
                <div className="app-notification-panel__header">
                  <div>
                    <strong>Notifications</strong>
                    <span>{unreadCount} non lue{unreadCount > 1 ? "s" : ""}</span>
                  </div>
                  <button type="button" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
                    Tout lu
                  </button>
                </div>

                <div className="app-notification-types">
                  {Object.entries(countsByType).map(([type, count]) => (
                    <span key={type}>{type}: {count}</span>
                  ))}
                </div>

                <div className="app-notification-list">
                  {notifications.length === 0 ? (
                    <div className="app-notification-empty">
                      Aucune notification pour le moment.
                    </div>
                  ) : (
                    notifications.slice(0, 12).map((notification) => (
                      <button
                        type="button"
                        key={notification.id}
                        className={`app-notification-item ${notification.is_read ? "is-read" : ""}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <span className={`app-notification-dot type-${notification.type}`} />
                        <span>
                          <strong>{notification.title}</strong>
                          <small>{notification.message}</small>
                          <em>
                            {notification.actor_name || "Systeme"}
                            {notification.actor_matricule ? ` - ${notification.actor_matricule}` : ""}
                          </em>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="app-user-button"
          onClick={() => navigate("/dashboard/profile")}
          aria-label="Ouvrir mon profil"
        >
          <UserIcon />
          <span>
            <strong>{displayName}</strong>
            <em>Matricule {user.matricule || "-"}</em>
          </span>
        </button>
        <button type="button" className="app-logout-button" onClick={handleLogout}>
          Deconnexion
        </button>
      </div>
    </header>
  );
}

function UserIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20 21a8 8 0 0 0-16 0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <circle
        cx="12"
        cy="7"
        r="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M18 9.8V11c0 2.2.6 3.5 1.4 4.5.5.6.1 1.5-.7 1.5H5.3c-.8 0-1.2-.9-.7-1.5C5.4 14.5 6 13.2 6 11V9.8C6 6.5 8.4 4 12 4s6 2.5 6 5.8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M9.8 19a2.4 2.4 0 0 0 4.4 0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}
